import { Listbox } from "@headlessui/react";
import React from "react";
import { MOBILE_BREAKPOINT } from "../config";
import { useTheme } from "../hooks/useTheme";
import styled, { css, ThemeProp } from "../styles";
import { SingleCheckBox } from "./Checkbox";
import { Chevron } from "./Chevron";
import { Flex } from "./Flex";
import { Text } from "./Text";

export interface DropdownOption {
  label: string;
  value: any;
  disabled?: boolean;
  comingSoon?: boolean;
  annotation?: React.ReactNode;
  isDivider?: boolean;
}

interface Props {
  margin?: string;
  options: DropdownOption[];
  selectedOptions: string[]; // Always an array of strings
  setSelectedOptions: (selected: string[]) => void;
  renderLabel: () => string;
  placeholder?: string;
  disableSelection?: boolean;
  selectionMode: "single" | "multiple";
  dropdownType?: "text" | "actions"; // actions is the 3 dots menu
  disableOptionSort?: boolean;
  width?: number;
  size?: "xs" | "s";
  showSelectAll?: boolean;
  anchor?: "top end" | "bottom end";
  onDropdownClick?: () => void;
  icon?: React.ReactNode;
}

const StyledListboxButton = styled(Listbox.Button)<{
  isDisabled?: boolean;
  dropdownType?: "text" | "actions";
  width?: number;
  size?: "xs" | "s";
  title?: string;
}>`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${(p) => p.theme.spacing.s} ${(p) => p.theme.spacing.m};
  cursor: pointer;
  font-family: ${(p) => p.theme.typography.bodyFamily};
  font-size: ${(p) => p.theme.typography.size.m};
  opacity: ${(p) => (p.isDisabled ? 0.5 : 1)};
  height: ${(p) => (p.size === "s" ? 36 : 42)}px;
  box-sizing: border-box;
  border-radius: ${(p) => p.theme.misc.borderRadius};

  &:focus-visible {
    outline: none !important;
  }

  ${(p) =>
    p.dropdownType === "text"
      ? css`
          background: transparent;
          border: none;
          border-radius: none !important;
          color: ${(p) => p.theme.color.primary};
          font-size: ${(p) => p.theme.typography.size.s};
          font-weight: ${(p) => p.theme.typography.weight.semi};
        `
      : p.dropdownType === "actions"
      ? css`
          background: transparent;
          border: none;
          color: ${(p) => p.theme.color.typography.secondary};
        `
      : css`
          color: ${(p) => p.theme.color.typography.text};
          width: ${(p: ThemeProp & { width?: number }) =>
            p.width ? `${p.width}px` : "100%"};
          height: ${(p: ThemeProp & { height?: number }) =>
            p.height ? p.height : 52}px;
          background-color: ${(p) => p.theme.color.card.background};
          border: 1px solid ${(p) => p.theme.color.typography.secondary}50;
          &:focus-visible {
            outline: none !important;
          }

          &:focus-within {
            outline: none !important;
          }

          &:hover {
            border-color: ${(p) => p.theme.color.typography.text};
            outline: none;
          }

          &[aria-expanded="true"] {
            border-color: ${(p) => p.theme.color.primary};
            outline: none;
          }

          &:focus,
          &:active {
            border-color: ${(p) => p.theme.color.primary};
            box-shadow: 0px 0px 0px 2px ${(p) => p.theme.color.input.hover};
            outline: none;
          }
        `}

  ${(p) =>
    p.size === "xs"
      ? css`
          height: 36px;
          font-size: ${(p) => p.theme.typography.size.s};
          border-radius: ${(p) => p.theme.misc.borderRadiusSmall};
        `
      : p.size === "s"
      ? css`
          height: 42px;
          font-size: ${(p) => p.theme.typography.size.m};
          border-radius: ${(p) => p.theme.misc.borderRadiusSmall};
        `
      : css`
          height: 44px;
          font-size: ${(p) => p.theme.typography.size.m};
          border-radius: ${(p) => p.theme.misc.borderRadius};
        `}

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    max-width: 100%;
  }

  :hover {
    .label {
      filter: brightness(0.8);
    }
  }
`;

const StyledListboxOptions = styled(Listbox.Options)<{
  dropdownType?: "text" | "actions";
  maxWidth?: number;
}>`
  background-color: ${(p) => p.theme.color.card.background};
  box-shadow: ${(p) => p.theme.shadow.cardHeavy};
  border-radius: ${(p) => p.theme.misc.borderRadius};
  margin-top: ${(p) => p.theme.spacing.xs};
  overflow-y: auto;
  max-height: 260px;
  min-width: 150px;
  max-width: 500px !important;
  width: auto !important;
  position: absolute;
  padding: 0px;
  z-index: 99999;

  ${(p) =>
    p.dropdownType === "text"
      ? css`
          right: 0px !important;
        `
      : css`
          left: 0px;
        `}

  &:focus-visible {
    outline: none !important;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    max-width: calc(100vw - 32px) !important;
    min-width: 200px;
  }
`;

const StyledListboxOption = styled(Listbox.Option)<{
  comingSoon?: boolean;
  isDisabled?: boolean;
}>`
  padding: ${(p) => p.theme.spacing.s} ${(p) => p.theme.spacing.m};
  cursor: ${(p) => (p.comingSoon || p.isDisabled ? "not-allowed" : "pointer")};
  opacity: ${(p) => (p.comingSoon || p.isDisabled ? 0.5 : 1)};
  display: flex;
  align-items: center;
  user-select: none;

  &:hover {
    background-color: ${(p) => p.theme.color.card.callout};
  }

  &:last-child {
    border-bottom-left-radius: ${(p) => p.theme.misc.borderRadius};
    border-bottom-right-radius: ${(p) => p.theme.misc.borderRadius};
  }

  &:first-child {
    border-top-left-radius: ${(p) => p.theme.misc.borderRadius};
    border-top-right-radius: ${(p) => p.theme.misc.borderRadius};
  }

  &:focus-visible {
    outline: none !important;
  }
`;

const MenuDividerRow = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${(p) => p.theme.color.card.divider};
`;

export function Dropdown({
  options,
  selectedOptions,
  setSelectedOptions,
  renderLabel,
  disableSelection,
  selectionMode,
  dropdownType,
  disableOptionSort,
  width,
  size,
  showSelectAll,
  anchor,
  onDropdownClick,
  icon,
}: Props) {
  const theme = useTheme();

  const handleSelect = (newValue: string | string[]) => {
    if (selectionMode === "multiple" && Array.isArray(newValue)) {
      setSelectedOptions(newValue);
    } else if (selectionMode === "single" && typeof newValue === "string") {
      setSelectedOptions([newValue]); // Wrap single selection in an array
    }
  };

  const handleSelectAll = () => {
    if (selectedOptions.length === options.length) {
      // If all are selected, deselect all
      setSelectedOptions([]);
    } else {
      // Otherwise, select all non-disabled and non-coming soon options
      const allValues = options
        .filter((option) => !option.disabled && !option.comingSoon)
        .map((option) => option.value);
      setSelectedOptions(allValues);
    }
  };

  const label = renderLabel();

  return (
    <div style={{ position: "relative" }}>
      <Listbox
        as="div"
        value={selectedOptions}
        onChange={handleSelect}
        multiple={selectionMode === "multiple"}
        disabled={disableSelection}
      >
        {({ open }) => (
          <>
            <StyledListboxButton
              isDisabled={disableSelection}
              width={width}
              size={size}
              dropdownType={dropdownType}
              title={label}
              onClick={(e: React.MouseEvent) => {
                if (onDropdownClick) {
                  onDropdownClick();
                }

                if (!disableSelection) {
                  e.stopPropagation();
                }
              }}
            >
              {dropdownType === "actions" ? (
                <Dots />
              ) : (
                <>
                  {icon && (
                    <div
                      style={{
                        position: "absolute",
                        left: theme.spacing.m,
                        pointerEvents: "none",
                        display: "flex",
                        alignItems: "center",
                        marginTop: -1,
                      }}
                    >
                      {icon}
                    </div>
                  )}
                  <span
                    style={{
                      marginRight: 20,
                      marginLeft: icon ? theme.spacing.xl : 0,
                      fontSize:
                        size === "xs" || size === "s"
                          ? theme.typography.size.s
                          : theme.typography.size.m,
                    }}
                    className="label"
                  >
                    {label}
                  </span>
                  <span
                    style={{ position: "absolute", right: 0 }}
                    className="label"
                  >
                    <Flex margin="0 m 0 0">
                      <Chevron
                        size="s"
                        style={{ marginLeft: "auto" }}
                        direction={open ? "up" : "down"}
                        animateTransition
                        color={
                          dropdownType === "text"
                            ? theme.color.primary
                            : theme.color.typography.secondary
                        }
                      />
                    </Flex>
                  </span>
                </>
              )}
            </StyledListboxButton>
            {open && (
              <StyledListboxOptions
                anchor={anchor ?? "bottom end"}
                style={width && { width: width }}
                maxWidth={width}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                dropdownType={dropdownType}
              >
                {showSelectAll && selectionMode === "multiple" && (
                  <SelectAllWrap
                    direction="row"
                    margin="0 s 0 0"
                    onClick={(e: React.MouseEvent<HTMLParagraphElement>) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSelectAll();
                    }}
                  >
                    <Text margin="0" weight="semi" size="s">
                      {selectedOptions.length === options.length
                        ? "Deselect all"
                        : "Select all"}
                    </Text>
                  </SelectAllWrap>
                )}
                {options
                  .sort((a, b) => {
                    if (disableOptionSort || a.isDivider || b.isDivider)
                      return 0;
                    if (a.comingSoon && !b.comingSoon) return 1;
                    if (!a.comingSoon && b.comingSoon) return -1;
                    // If labels are the same, use value as tiebreaker to ensure consistent ordering
                    return a.label === b.label
                      ? String(a.value) > String(b.value)
                        ? 1
                        : -1
                      : a.label > b.label
                      ? 1
                      : -1;
                  })
                  .map((option, index) => {
                    if (option.isDivider) {
                      return (
                        <MenuDividerRow
                          key={`divider-${index}`}
                          role="presentation"
                        />
                      );
                    }
                    return (
                      <StyledListboxOption
                        isDisabled={option.disabled}
                        key={option.value}
                        value={option.value}
                        comingSoon={option.comingSoon}
                        onClick={(e: React.MouseEvent) => {
                          if (option.comingSoon) {
                            e.preventDefault();
                            e.stopPropagation();
                          }
                        }}
                      >
                        {({ selected }) => (
                          <Flex
                            direction="row"
                            margin="0 s 0 0"
                            align="center"
                            justify="space-between"
                            style={{ width: "100%" }}
                          >
                            <Flex direction="row" margin="0 s 0 0">
                              {selectionMode === "multiple" ? (
                                <Flex
                                  style={{ width: 20 }}
                                  margin="0 s 0 0"
                                  align="center"
                                  justify="center"
                                >
                                  <SingleCheckBox
                                    checked={selected}
                                    size={"s"}
                                  />
                                </Flex>
                              ) : null}
                              <Text
                                size={dropdownType === "actions" ? "s" : "m"}
                                weight={
                                  dropdownType === "actions" ? "semi" : "normal"
                                }
                                margin="0"
                                truncate
                              >
                                {option.label}
                              </Text>
                            </Flex>
                            {option.comingSoon ? (
                              <Text
                                size="xs"
                                margin="0 0 0 m"
                                isInline
                                colorPreset="reward"
                              >
                                Coming Soon
                              </Text>
                            ) : null}
                            {option.annotation ? option.annotation : null}
                          </Flex>
                        )}
                      </StyledListboxOption>
                    );
                  })}
              </StyledListboxOptions>
            )}
          </>
        )}
      </Listbox>
    </div>
  );
}

const Dot = styled.div`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background-color: ${(p) => p.theme.color.typography.secondary};
  margin: 1px 0px;
`;

function Dots() {
  return (
    <DotsWrap direction="column" align="center" justify="center">
      <Dot />
      <Dot />
      <Dot />
    </DotsWrap>
  );
}

const SelectAllWrap = styled(Flex)`
  cursor: pointer;
  border-bottom: 1px solid ${(p) => p.theme.color.typography.secondary}20;
  padding: ${(p) => p.theme.spacing.s} ${(p) => p.theme.spacing.m};
  display: flex;
  align-items: center;
  user-select: none;
  position: sticky;
  top: 0;
  background-color: ${(p) => p.theme.color.card.background};
  z-index: 1;

  :hover {
    background-color: ${(p) => p.theme.color.card.callout};
  }
`;

const DotsWrap = styled(Flex)`
  border-radius: ${(p) => p.theme.misc.borderRadiusXSmall};
  padding: ${(p) => p.theme.spacing.s};

  :hover {
    background-color: ${(p) => p.theme.color.card.callout};
  }
`;
